// École Nid Douillet - Header Component
// Top navigation bar with user menu and notifications

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@store/authStore';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  Bars3Icon,
  BellIcon,
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { t } = useTranslation(['common', 'auth']);
  const { user, logout } = useAuth();
  const [notifications] = useState([]); // TODO: Implement notifications

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header className="bg-white shadow-sm border-b border-neutral-200">
      <div className="flex h-16 items-center justify-between px-6">
        {/* Left side - Mobile menu button */}
        <div className="flex items-center">
          <button
            onClick={onMenuClick}
            className="lg:hidden rounded-md p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>

        {/* Center - Page title or breadcrumbs */}
        <div className="flex-1 min-w-0 px-4">
          <h1 className="text-lg font-semibold text-neutral-900 truncate">
            {/* This will be updated based on current page */}
            {t('navigation.dashboard')}
          </h1>
        </div>

        {/* Right side - Notifications and user menu */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <button className="relative rounded-full p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600">
            <BellIcon className="h-6 w-6" />
            {notifications.length > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-xs text-white flex items-center justify-center">
                {notifications.length}
              </span>
            )}
          </button>

          {/* User menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center space-x-3 rounded-full p-2 text-sm hover:bg-neutral-100">
              <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-700 font-medium text-sm">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </span>
              </div>
              <span className="hidden md:block text-neutral-700 font-medium">
                {user?.firstName} {user?.lastName}
              </span>
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-neutral-100 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="px-4 py-3">
                  <p className="text-sm font-medium text-neutral-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-sm text-neutral-500">
                    {user?.email}
                  </p>
                  <p className="text-xs text-neutral-400 mt-1">
                    {t(`auth:roles.${user?.role}`)}
                  </p>
                </div>

                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <a
                        href="/profile"
                        className={clsx(
                          'group flex items-center px-4 py-2 text-sm',
                          active ? 'bg-neutral-100 text-neutral-900' : 'text-neutral-700'
                        )}
                      >
                        <UserIcon className="mr-3 h-5 w-5 text-neutral-400" />
                        {t('navigation.profile')}
                      </a>
                    )}
                  </Menu.Item>

                  {user?.role === 'DIRECTOR' && (
                    <Menu.Item>
                      {({ active }) => (
                        <a
                          href="/settings"
                          className={clsx(
                            'group flex items-center px-4 py-2 text-sm',
                            active ? 'bg-neutral-100 text-neutral-900' : 'text-neutral-700'
                          )}
                        >
                          <Cog6ToothIcon className="mr-3 h-5 w-5 text-neutral-400" />
                          {t('navigation.settings')}
                        </a>
                      )}
                    </Menu.Item>
                  )}
                </div>

                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={clsx(
                          'group flex w-full items-center px-4 py-2 text-sm',
                          active ? 'bg-neutral-100 text-neutral-900' : 'text-neutral-700'
                        )}
                      >
                        <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-neutral-400" />
                        {t('navigation.logout')}
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  );
};

export default Header;
